import { useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";

type SpeechResult = { isFinal: boolean; [index: number]: { transcript: string } };
type Recognition = {
  lang: string; continuous: boolean; interimResults: boolean;
  onresult: ((event: { resultIndex: number; results: ArrayLike<SpeechResult> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void; stop: () => void; abort: () => void;
};
type SpeechWindow = { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };

export function DictationButton({ value, onChange, label, disabled = false, maxLength = 4000 }: {
  value: string; onChange: (value: string) => void; label: string; disabled?: boolean; maxLength?: number;
}) {
  const [listening, setListening] = useState(false);
  const [message, setMessage] = useState("");
  const recognition = useRef<Recognition | null>(null);
  const latest = useRef({ value, onChange, disabled, maxLength });
  latest.current = { value, onChange, disabled, maxLength };
  const Constructor = typeof window === "undefined" ? undefined : (window as unknown as SpeechWindow).SpeechRecognition ?? (window as unknown as SpeechWindow).webkitSpeechRecognition;
  const abort = () => {
    const current = recognition.current;
    recognition.current = null;
    if (current) { current.onresult = null; current.onerror = null; current.onend = null; current.abort(); }
  };
  useEffect(() => () => abort(), []);
  useEffect(() => { if (disabled) { abort(); setListening(false); } }, [disabled]);
  function start() {
    if (!Constructor || disabled || recognition.current) return;
    const current = new Constructor();
    recognition.current = current;
    current.lang = document.documentElement.lang || navigator.language || "en-US";
    current.continuous = false;
    current.interimResults = false;
    const processed = new Set<number>();
    setMessage("");
    current.onresult = event => {
      if (recognition.current !== current || latest.current.disabled) return;
      for (let index = event.resultIndex; index < event.results.length; index++) {
        if (!event.results[index].isFinal || processed.has(index)) continue;
        processed.add(index);
        const words = event.results[index][0].transcript.trim();
        if (!words) continue;
        const previous = latest.current.value;
        const next = `${previous}${previous && !/\s$/.test(previous) ? " " : ""}${words}`;
        const limited = next.slice(0, latest.current.maxLength);
        latest.current.value = limited;
        latest.current.onChange(limited);
        if (next.length > limited.length) setMessage("Text limit reached. Review the last sentence.");
      }
    };
    current.onerror = event => {
      if (recognition.current !== current) return;
      setMessage(event.error === "not-allowed" || event.error === "service-not-allowed" ? "Microphone access was denied. Allow it in your browser or use the keyboard microphone." : event.error === "no-speech" ? "No speech heard. Tap Talk to text to try again." : "Dictation stopped. Try again or use the keyboard microphone.");
      abort(); setListening(false);
    };
    current.onend = () => { if (recognition.current === current) { recognition.current = null; setListening(false); } };
    try { current.start(); setListening(true); }
    catch { abort(); setListening(false); setMessage("Dictation could not start. Try again or use the keyboard microphone."); }
  }
  return <div className="mt-1 text-xs">
    <button type="button" disabled={disabled || !Constructor} aria-label={`${listening ? "Stop dictation for" : "Dictate"} ${label}`} aria-pressed={listening}
      onClick={() => listening ? recognition.current?.stop() : start()} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 font-semibold text-sky-900 hover:bg-sky-100 disabled:opacity-50">
      {listening ? <Square size={16} aria-hidden="true" /> : <Mic size={16} aria-hidden="true" />}{listening ? "Stop dictation" : "Talk to text"}
    </button>
    {!Constructor && <p className="mt-1 text-[#6b6b6b]">Use the microphone on your phone keyboard to dictate.</p>}
    {(listening || message) && <p role="status" className="mt-1">{message || "Listening… Speak, then review the text."}</p>}
  </div>;
}
